import { ICON_SIZE_LARGE, ICON_SIZE_MEDIUM } from '@carto/meridian-ds/theme'
import { styled } from '@mui/material'
import { ReactElement, ReactNode } from 'react'

type IconWraperProps = {
  backgroundColor?: string
  iconColor?: string
  size?: RoundedIconSize
  disabled?: boolean
  border?: boolean
}

const IconWraper = styled('div', {
  shouldForwardProp: (prop: string) => !['size', 'backgroundColor', 'iconColor', 'disabled', 'border'].includes(prop)
})<IconWraperProps>(({ backgroundColor, iconColor, size, disabled, border, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing(4),
  minWidth: theme.spacing(4),
  height: theme.spacing(4),
  backgroundColor: backgroundColor || theme.palette.grey[50],
  borderRadius: '50%',

  svg: {
    width: ICON_SIZE_MEDIUM,
    height: ICON_SIZE_MEDIUM,

    path: {
      fill: iconColor || theme.palette.common.black
    }
  },
  '&[data-state="selected"]': {
    backgroundColor: theme.palette.secondary.relatedLight,

    '& svg path': {
      fillOpacity: 1,
      fill: theme.palette.secondary.dark
    }
  },

  ...(size === 'large' && {
    // TODO: review with design if we can simplify the sizes variations
    '& svg': {
      width: ICON_SIZE_LARGE,
      height: ICON_SIZE_LARGE,
      transformOrigin: '50% 50%',
      transform: 'scale(0.8)',
      fillOpacity: 0.4,
      transition: theme.transitions.create(['fill', 'fill-opacity'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shortest
      })
    },
    [theme.breakpoints.up('md')]: {
      minWidth: theme.spacing(6),
      width: theme.spacing(6),
      height: theme.spacing(6),

      '& svg': {
        transform: 'scale(1)'
      }
    }
  }),
  ...(size === 'xsmall' && {
    width: theme.spacing(3),
    minWidth: theme.spacing(3),
    height: theme.spacing(3)
  }),
  ...(size === 'small' && {
    width: theme.spacing(4),
    minWidth: theme.spacing(4),
    height: theme.spacing(4)
  }),
  ...(size === 'medium' && {
    width: theme.spacing(5),
    minWidth: theme.spacing(5),
    height: theme.spacing(5)
  }),
  ...(border && {
    border: `1px solid ${theme.palette.default.outlinedBorder}`
  }),
  ...(disabled && {
    backgroundColor: theme.palette.grey[50],

    '& svg path': {
      fill: theme.palette.text.disabled
    }
  })
}))

export type RoundedIconSize = 'xsmall' | 'small' | 'medium' | 'large'
export interface RoundedIconProps {
  icon: ReactElement | ReactNode
  state?: string
  className?: string
  size?: RoundedIconSize
  iconColor?: string
  backgroundColor?: string
  disabled?: boolean
  border?: boolean
}

function RoundedIcon({
  className,
  icon,
  iconColor,
  backgroundColor,
  state,
  size = 'large',
  disabled,
  border
}: RoundedIconProps) {
  return (
    <IconWraper
      className={className}
      size={size}
      iconColor={iconColor}
      backgroundColor={backgroundColor}
      data-state={state}
      disabled={disabled}
      border={border}
    >
      {icon}
    </IconWraper>
  )
}

export default RoundedIcon
